import { useMemo } from "react";
import { useUserModule } from "../../../contexts/UserModuleContext";
import { sortChangeLogDescending } from "../../../utils/userModuleUtils";
import "../../../css/UserProfileTabs.css";

interface ChangeLogTabProps {
    user: any;
}

/**
 * ChangeLogTab — Chronological activity log showing all profile modifications.
 * Entries sorted newest first with old→new value highlights.
 *
 * Validates: Requirements 11.1, 11.2, 11.3
 */
export const ChangeLogTab = ({ user }: ChangeLogTabProps) => {
    const { changeLogEntries } = useUserModule();

    // Filter entries for this user and sort descending
    const sortedEntries = useMemo(() => {
        const userEntries = changeLogEntries.filter((e) => e.userId === user?.id);
        return sortChangeLogDescending(userEntries);
    }, [changeLogEntries, user?.id]);

    const formatDate = (dateStr?: string | null): string => {
        if (!dateStr) return "—";
        try {
            return new Date(dateStr).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
            });
        } catch {
            return dateStr;
        }
    };

    return (
        <div className="upt-changelog-tab">
            <div className="upt-section" style={{ gridColumn: "1 / -1" }}>
                <h3 className="upt-section-title">Activity Log</h3>
                {sortedEntries.length === 0 ? (
                    <div className="upt-empty-state">
                        No changes recorded yet. Modifications to this user's profile will appear here.
                    </div>
                ) : (
                    <div className="upt-changelog-list">
                        {sortedEntries.map((entry) => (
                            <div key={entry.id} className="upt-changelog-entry">
                                <div className="upt-changelog-date">
                                    {formatDate(entry.createdAt)}
                                </div>
                                <div className="upt-changelog-content">
                                    <div className="upt-changelog-bullet" />
                                    <div className="upt-changelog-details">
                                        <span className="upt-changelog-description">
                                            {entry.description}
                                        </span>
                                        {(entry.oldValue || entry.newValue) && (
                                            <div className="upt-changelog-values">
                                                {entry.oldValue && (
                                                    <span className="upt-changelog-old-value">
                                                        {entry.oldValue}
                                                    </span>
                                                )}
                                                {entry.oldValue && entry.newValue && (
                                                    <span className="upt-changelog-arrow">→</span>
                                                )}
                                                {entry.newValue && (
                                                    <span className="upt-changelog-new-value">
                                                        {entry.newValue}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <span className="upt-changelog-author">
                                            by {entry.changedByName}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
